/* eslint-disable @typescript-eslint/no-explicit-any */
import { RootState } from '@/app/store';
import { IPlaylistId, ITracksId, PlaylistState } from '@/interface';
import { beezerApi } from '@/services/deezer';
import { spotifyApi } from '@/services/spotify';
import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';

const initialState: PlaylistState = {
  playlists: [],
  status: {
    userPlaylistState: 'loading',
    playlistsState: 'loading',
    playlistIdState: 'loading'
  },
  userPlayList: [],
  playlistsId: {} as IPlaylistId
};

export const fetchUserPlaylists = createAsyncThunk(
  'playlist/fetchPlaylists',
  async () => {
    const response = await spotifyApi.getUserPlaylists();
    return response.body.items;
  }
);

export const fetchPlaylistById = createAsyncThunk(
  'playlist/fetchPlaylistById',
  async (playlistId: string) => {
    const response = await spotifyApi.getPlaylist(playlistId);
    return response.body;
  }
);

export const fetchFeaturePlaylists = createAsyncThunk(
  'playlist/fetchFeaturePlaylists',
  async () => {
    const response = await spotifyApi.getFeaturedPlaylists({
      limit: 14,
      offset: 1,
      country: 'PE',
      locale: 'sv_SE',
      timestamp: '2014-10-23T09:00:00'
    });
    return response.body.playlists.items;
  }
);

export const playlistBeezer = createAsyncThunk(
  'playlist/playlistDeezer',
  async () => {
    const response = await beezerApi('chart/0/albums');
    const result = await response.json();
    return result.data.map((item: any) => {
      return {
        ...item,
        images: [
          {
            url: item.cover_medium
          }
        ],
        name: item?.title
      };
    });
  }
);

export const playlistByIdBeezer = createAsyncThunk(
  'playlist/playlistByIdBeezer',
  async (playlistId: string) => {
    const response = await beezerApi(`playlist/${playlistId}`);
    const result = await response.json();
    return {
      ...result,
      name: result.title,
      owner: {
        display_name: result.creator.name
      },
      images: [
        {
          url: result.picture_medium
        }
      ],
      followers: {
        total: result.fans
      },
      tracks: {
        items: result.tracks.data.map((item: any) => {
          return {
            ...item,
            track: {
              name: item.title,
              album: {
                ...item.album,
                images: [
                  {
                    url: item.album.cover
                  }
                ]
              },
              artists: [
                {
                  ...item.artist,
                  name: item.artist.name
                }
              ],
              preview_url: item.preview,
              id: item.id
            }
          };
        })
      }
    };
  }
);

export const playlistSlice = createSlice({
  name: 'playlist',
  initialState,
  reducers: {
    setFavorite: {
      reducer(
        state: PlaylistState,
        action: PayloadAction<ITracksId, string, { isFavorite: boolean }>
      ) {
        // get favorite
        if (!state.playlistsId.tracks?.items) return;
        state.playlistsId.tracks.items = [
          ...state.playlistsId.tracks.items.map((item) => {
            if (item.track.id === action.payload.track.id) {
              // item.track.isFavorite = action.meta.isFavorite
              return {
                ...item,
                track: {
                  ...item.track,
                  isFavorite: action.meta.isFavorite ? true : false
                }
              };
            }
            return item;
          })
        ];
      },
      prepare(tracksId: ITracksId, isFavorite: boolean) {
        return {
          payload: tracksId,
          meta: { isFavorite }
        };
      }
    },
    setPlaylistId: (state: PlaylistState, action: PayloadAction<any>) => {
      state.playlistsId.tracks = action.payload as any;
    },
    clearPlaylistAll: (state: PlaylistState) => {
      state.playlists = [] as any;
      state.playlistsId.tracks = {} as any;
    }
  },
  extraReducers: (builder) => {
    builder.addCase(fetchUserPlaylists.pending, ({ status }) => {
      status.userPlaylistState = 'loading';
    });
    builder.addCase(fetchUserPlaylists.fulfilled, (state, action) => {
      state.userPlayList = action.payload as [];
      state.status.userPlaylistState = 'ok';
    });
    builder.addCase(fetchUserPlaylists.rejected, ({ status }) => {
      status.userPlaylistState = 'failed';
    });
    builder.addCase(fetchFeaturePlaylists.pending, ({ status }) => {
      status.playlistsState = 'loading';
    });
    builder.addCase(fetchFeaturePlaylists.rejected, ({ status }) => {
      status.playlistsState = 'failed';
    });
    builder.addCase(fetchFeaturePlaylists.fulfilled, (state, action) => {
      state.playlists = action.payload as [];
      state.status.playlistsState = 'ok';
    });
    builder.addCase(fetchPlaylistById.pending, ({ status }) => {
      status.playlistIdState = 'loading';
    });
    builder.addCase(fetchPlaylistById.rejected, ({ status }) => {
      status.playlistIdState = 'failed';
    });
    builder.addCase(fetchPlaylistById.fulfilled, (state, action) => {
      state.playlistsId = action.payload as unknown as IPlaylistId;
      state.status.playlistIdState = 'ok';
    });
    builder.addCase(playlistBeezer.pending, ({ status }) => {
      status.playlistsState = 'loading';
    });
    builder.addCase(playlistBeezer.rejected, ({ status }) => {
      status.playlistsState = 'failed';
    });
    builder.addCase(playlistBeezer.fulfilled, (state, action) => {
      state.playlists = action.payload;
      state.status.playlistsState = 'ok';
    });
    builder.addCase(playlistByIdBeezer.pending, ({ status }) => {
      status.playlistIdState = 'loading';
    });
    builder.addCase(playlistByIdBeezer.rejected, ({ status }) => {
      status.playlistIdState = 'failed';
    });
    builder.addCase(playlistByIdBeezer.fulfilled, (state, action) => {
      state.playlistsId = action.payload;
      state.status.playlistIdState = 'ok';
    });
  }
});

export const { setFavorite, setPlaylistId, clearPlaylistAll } =
  playlistSlice.actions;
export const selectPlaylist = (state: RootState) => state.playlist;

export default playlistSlice.reducer;
